import { ReportArea } from 'app/area/reports/containers/chart/components/chart-area';
import { SelectionReportChart } from 'app/area/reports/containers/chart/components/selection-report-chart';
import { ChartState } from 'app/area/reports/containers/chart/types-store';
import * as React from 'react';
import { Switch } from 'ui/common/switch/switch';
import styles from './chart-container.scss';
import { DrillDownModel } from 'app/components/favourite-layout/favourite/common/drill-down-model';
import { ComplexValue, ElementLoader } from 'ui';
import { SelectionFieldItem } from 'app/area/reports/containers/group-by/types-fields';
import { Value } from 'app/types-shared';

interface ChartViewProps {
  chartState: ChartState;
  layoutAttributes: {
    [x: string]: string | boolean;
  };
  translation: any;
  autoExecute: boolean;
  selectedAggregationField: ComplexValue<number>;
  selectedGroupByFields: SelectionFieldItem[];
  drillDown: (favouriteId: number, model: DrillDownModel) => void;
  intl: any;
  toggleAutoExecution: () => void;
  updateLayoutAttributes: (attributes: { [key: string]: string | boolean }[]) => void;
  setLayoutType: (type: Value) => void;
  selectedLayoutType: ComplexValue<number>;
  hideVisualizationOptions?: boolean;
  requestCustomWidth: (id: number, val: any) => void;
  reflowHandle: string;
  height?: number;
  layoutAttributeOptions?:  {
    [x: string]: string | boolean;
  };
  drillDownReport?: ComplexValue<number>;
  setDrillDownReport?: (drillDownReport: ComplexValue<number>) => void;
  onLoadDrillDownOptions?: () => void;
}

export const ChartView = ({
  chartState,
  layoutAttributes,
  translation,
  autoExecute,
  selectedLayoutType,
  selectedAggregationField,
  selectedGroupByFields,
  drillDown,
  intl,
  toggleAutoExecution,
  updateLayoutAttributes,
  setLayoutType,
  hideVisualizationOptions,
  requestCustomWidth,
  reflowHandle,
  height,
  layoutAttributeOptions,
  drillDownReport,
  setDrillDownReport,
  onLoadDrillDownOptions
}: ChartViewProps) => {

  const {
    fetchingExecute,
    selectionReport,
    layoutTypes,
    selectionReportLayout,
    errorsExecuteFailed
  } = chartState;
  
  const showResult = autoExecute && !!selectedAggregationField?.value
    && !!selectionReportLayout && selectedLayoutType?.value === selectionReportLayout.layoutType;
  const numberOfGroupByFields = selectedGroupByFields.length;
  const presentationType = !!selectionReportLayout && selectionReportLayout.presentationType;
  const hasExecuteFailedErrors = !!errorsExecuteFailed && !!errorsExecuteFailed.length;

  const information = (
      <div className={styles.uiToggleInfo}>
        <div className={styles.title}>{translation.toggle_info_title}</div>
        <div className={styles.subTitle}>{translation.toggle_info_subtitle}</div>
        <div className={styles.description}>{translation.toggle_info_description}</div>
      </div>
    );

  const failExecuteInformation = (
    <div className={`${styles.uiToggleInfo} ${styles.warningInfo}`}>
      <div className={`${styles.title} ${styles.warningMsg}`}>{translation.toggle_info_title}</div>
      <div className={`${styles.subTitle} ${styles.warningMsg}`}>{errorsExecuteFailed}</div>
      <div className={`${styles.warningDescription}`}>{translation.execute_failed_info_description}</div>
    </div>
  );

  const result = <SelectionReportChart
        layoutTypes={layoutTypes}
        selectedLayoutType={selectedLayoutType?.value}
        numberOfGroupByFields={numberOfGroupByFields}
        selectionReport={selectionReport}
        presentationType={presentationType}
        layoutAttributes={layoutAttributes}
        drillDown={drillDown}
        requestCustomWidth={requestCustomWidth}
        reflowHandle={reflowHandle}
        intl={intl}
        translation={translation}
        height={height} />;
  
  const resultOrInfo = autoExecute ? 
    (hasExecuteFailedErrors ? failExecuteInformation : (!!showResult ? result : (fetchingExecute && <ElementLoader />))) : 
    information;

  return hideVisualizationOptions ? resultOrInfo : (
    <div className={`${styles.chartContainer}`}>
      <div className={styles.switch}>
        <Switch
          className={styles.selectSwitch}
          checked={autoExecute}
          onChange={toggleAutoExecution}
          label={translation.toggle_info_load_options}
        />
      </div>
      <ReportArea
        fetchingExecute={autoExecute && chartState.fetchingExecute}
        layoutTypes={layoutTypes}
        selectedLayoutType={selectedLayoutType?.value}
        presentationType={presentationType}
        layoutAttributes={layoutAttributeOptions}
        numberOfGroupByFields={numberOfGroupByFields}
        translation={translation}
        onSetLayoutType={setLayoutType}
        onAttributeChange={updateLayoutAttributes}
        fetchingOptionsDrillDownTo={chartState?.fetchingOptionsDrillDownTo}
        optionsDrillDownTo={chartState?.optionsDrillDownTo}
        drillDownReport={drillDownReport}
        setDrillDownReport={setDrillDownReport}
        onLoadDrillDownOptions={onLoadDrillDownOptions}>
      
        {resultOrInfo}
      </ReportArea>
   </div>
  );
};
